import {
  AddAppData,
  AppInstallOption,
  IntentInstallOptions,
} from '@wix/editor-platform-host-integration-apis';
import { noop } from 'lodash';
import { getOrigin } from '../helpers';
import { IntentInstallStore } from '../IntentInstallStore';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import {
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import { isTpaWidgetApp } from '../../../utils/appMarketUtils';

function createInstallCallback(
  appDefId: string,
  intentInstallStore: IntentInstallStore,
  onResultsCaptured: (appDefId: string) => void,
): ((addedAppData: AddAppData) => void) | undefined {
  return (addedAppData: AddAppData) => {
    if (addedAppData?.success === true) {
      intentInstallStore.setInstallationResults(appDefId, addedAppData);
      onResultsCaptured(appDefId);
    }
  };
}

const getPendingAppOverrides = (
  documentServicesAPI: DocumentServicesObject,
  appDefId: string,
) => {
  const appData: EditorClientSpecMapEntry | undefined =
    documentServicesAPI.platform.getAppDataByAppDefId(
      appDefId,
    ) as unknown as EditorClientSpecMapEntry;

  const pendingAppVersion = appData?.appFields?.installedVersion;
  const overrideId =
    pendingAppVersion === 'dev' ? appData?.appFields?.overrideId : undefined;
  return { pendingAppVersion, overrideId };
};

export function getCommonAppOptions(
  intentInstallStore: IntentInstallStore,
  documentServicesAPI: DocumentServicesObject,
  appDefIds: string[],
  options: IntentInstallOptions,
  _experimentsAPI: IExperimentsApiAdapter,
  onResultsCaptured: (appDefId: string) => void,
): Record<string, AppInstallOption> {
  const { appsOptions } = options;
  const origin = getOrigin(options);
  const intentApp = intentInstallStore.getIntentApp();

  return appDefIds.reduce((acc, appDefId) => {
    const appVersionIfIntent =
      appDefId === intentApp?.appDefId ? intentApp.version : undefined;

    const appOptions = appsOptions?.[appDefId];

    const { pendingAppVersion, overrideId } = getPendingAppOverrides(
      documentServicesAPI,
      appDefId,
    );

    const callback = createInstallCallback(
      appDefId,
      intentInstallStore,
      onResultsCaptured,
    );

    acc[appDefId] = {
      origin,
      isSilent: options.isSilent ?? false,
      appVersion:
        pendingAppVersion || appOptions?.appVersion || appVersionIfIntent,
      ...(overrideId ? { overrideId } : {}),
      pluginsAutoPopulate: { isSilent: options.isSilent ?? false },
      ...(appOptions || {}),
      ...(callback ? { callback } : {}),
    };
    return acc;
  }, {} as Record<string, AppInstallOption>);
}

export const createPromiseWithResolvers = <T>() => {
  let resolve: (arg: T) => void = noop;
  let reject: (reason?: unknown) => void = noop;

  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
};

export const shouldSkipActiveApps = (
  documentServicesAPI: DocumentServicesObject,
  apps: string[],
) => {
  return !apps.every((app) => {
    const appData = documentServicesAPI.platform.getAppDataByAppDefId(app) as
      | EditorClientSpecMapEntry
      | undefined;
    return appData && isTpaWidgetApp(appData);
  });
};
